import store from '@/store';
import { addToScene, removeFromScene } from '@/utils/forgeUtils';
import { screenScaleForTarget } from '@/utils/markerScreenSpace';

const OVERLAY_NAME = 'ai-finding-position-preview';
const TEXTURE_SIZE = 256;
const MAX_MARKER_SIZE_PX = 48;
const MIN_MARKER_SIZE_PX = 40;
const activePreviewsByViewer = new WeakMap();

function drawOutlinedPath(context) {
  for (const [color, lineWidth] of [['#111111', 18], ['#ffffff', 12], ['#ffb300', 7]]) {
    context.strokeStyle = color;
    context.lineWidth = lineWidth;
    context.stroke();
  }
}

function drawOutlinedCircle(context, radius) {
  context.beginPath();
  context.arc(128, 128, radius, 0, Math.PI * 2);
  drawOutlinedPath(context);
}

function drawTargetLines(context) {
  context.beginPath();
  context.moveTo(128, 32);
  context.lineTo(128, 64);
  context.moveTo(192, 128);
  context.lineTo(224, 128);
  context.moveTo(128, 192);
  context.lineTo(128, 224);
  context.moveTo(32, 128);
  context.lineTo(64, 128);
  context.lineCap = 'round';
  drawOutlinedPath(context);
}

function createCanvasTexture(THREE, draw) {
  const canvas = window.document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
  draw(context);
  const texture = new THREE.Texture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function createReticleTexture(THREE) {
  return createCanvasTexture(THREE, context => {
    drawOutlinedCircle(context, 76);
    context.beginPath();
    context.arc(128, 128, 8, 0, Math.PI * 2);
    context.fillStyle = '#ffb300';
    context.fill();
    drawTargetLines(context);
  });
}

function createMarkerPlane(THREE, texture) {
  return new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
}

function projectedScale(viewer, camera, point, targetPixels, worldSpan, THREE) {
  if (typeof viewer?.worldToClient !== 'function') return null;
  const basePosition = new THREE.Vector3(point.x, point.y, point.z);
  const span = new THREE.Vector3(0, worldSpan, 0).applyQuaternion(camera.quaternion);
  const baseClient = viewer.worldToClient(basePosition, camera);
  const endClient = viewer.worldToClient(basePosition.clone().add(span), camera);
  const spanScale = screenScaleForTarget(baseClient, endClient, targetPixels);
  return spanScale === null ? null : spanScale * worldSpan;
}

export function showFindingPositionPreview(viewer, point) {
  activePreviewsByViewer.get(viewer)?.clear();
  const THREE = window.THREE;
  const reticleTexture = createReticleTexture(THREE);
  const marker = createMarkerPlane(THREE, reticleTexture);
  marker.position.set(point.x, point.y, point.z);
  addToScene(marker, viewer, OVERLAY_NAME);
  const unitScale = Number(store.state.unitScale);
  const worldSpan = Number.isFinite(unitScale) && unitScale > 0 ? 1 / unitScale : 1;
  const initialCamera = viewer?.getCamera?.();
  const initialPixelScale = initialCamera
    ? projectedScale(viewer, initialCamera, point, 1, worldSpan, THREE)
    : null;
  const cameraEvent = window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT;
  const faceCamera = () => {
    const camera = viewer?.getCamera?.();
    if (!camera) return;
    marker.quaternion.copy(camera.quaternion);
    const currentPixelScale = projectedScale(viewer, camera, point, 1, worldSpan, THREE);
    const zoomRatio = initialPixelScale && currentPixelScale
      ? Math.sqrt(currentPixelScale / initialPixelScale)
      : 1;
    const markerPixels = Math.min(
      MAX_MARKER_SIZE_PX,
      Math.max(MIN_MARKER_SIZE_PX, MAX_MARKER_SIZE_PX * zoomRatio),
    );
    const markerSize = projectedScale(viewer, camera, point, markerPixels, worldSpan, THREE) || 0.1;
    marker.scale.set(markerSize, markerSize, 1);
    viewer?.impl?.invalidate?.(true);
  };
  if (cameraEvent && typeof viewer?.addEventListener === 'function') {
    viewer.addEventListener(cameraEvent, faceCamera);
  }
  faceCamera();
  viewer?.impl?.invalidate?.(true, true, true);

  let cleared = false;
  const handle = {
    clear() {
      if (cleared) return;
      cleared = true;
      if (cameraEvent && typeof viewer?.removeEventListener === 'function') {
        viewer.removeEventListener(cameraEvent, faceCamera);
      }
      reticleTexture.dispose();
      removeFromScene(marker, viewer, OVERLAY_NAME);
      viewer?.impl?.invalidate?.(true, true, true);
      if (activePreviewsByViewer.get(viewer) === handle) {
        activePreviewsByViewer.delete(viewer);
      }
    },
  };
  activePreviewsByViewer.set(viewer, handle);
  return handle;
}
